import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import {
  Check,
  CheckCheck,
  ChevronLeft,
  ChevronRight,
  Loader2,
  Mail,
  MessageSquare,
  Trash2,
} from "lucide-react";
import { useState } from "react";
import { toast } from "sonner";

import { client } from "@/utils/orpc";
import { Badge } from "@super-resume-new/ui/components/badge";
import { Button } from "@super-resume-new/ui/components/button";
import { Card } from "@super-resume-new/ui/components/card";
import { seo } from "@/utils/seo";

export const Route = createFileRoute("/admin/contact")({
  head: () =>
    seo({
      title: "Contact Submissions | JobCues Admin",
      description: "Manage and respond to candidate inquiries, partnership notes, and support tickets.",
      path: "/admin/contact",
      noindex: true,
    }),
  component: AdminContactPage,
});

function AdminContactPage() {
  const queryClient = useQueryClient();
  const [page, setPage] = useState(1);
  const [statusFilter, setStatusFilter] = useState<"all" | "new" | "read">("all");
  const pageSize = 15;

  const { data, isLoading, refetch } = useQuery({
    queryKey: ["admin", "contact", page, statusFilter],
    queryFn: () =>
      client.contact.list({
        page,
        limit: pageSize,
        status: statusFilter,
      }),
  });

  const markReadMutation = useMutation({
    mutationFn: async (id: string) => {
      return client.contact.markRead({ id });
    },
    onSuccess: () => {
      toast.success("Marked as read");
      refetch();
      queryClient.invalidateQueries({ queryKey: ["admin", "stats"] });
    },
    onError: (err: any) => toast.error(err.message || "Failed to mark read"),
  });

  const deleteMutation = useMutation({
    mutationFn: async (id: string) => {
      return client.contact.delete({ id });
    },
    onSuccess: () => {
      toast.success("Submission deleted");
      refetch();
      queryClient.invalidateQueries({ queryKey: ["admin", "stats"] });
    },
    onError: (err: any) => toast.error(err.message || "Failed to delete submission"),
  });

  const submissions = data?.items ?? [];
  const total = data?.total ?? 0;
  const unreadCount = data?.unreadCount ?? 0;
  const totalPages = data?.totalPages ?? 1;

  const handleMarkAllRead = async () => {
    const unreadIds = submissions.filter((s: any) => s.status === "new").map((s: any) => s.id);
    if (unreadIds.length === 0) return;
    try {
      await client.contact.batchMarkRead({ ids: unreadIds });
      toast.success(`Marked ${unreadIds.length} submissions as read`);
      refetch();
      queryClient.invalidateQueries({ queryKey: ["admin", "stats"] });
    } catch (err: any) {
      toast.error(err.message || "Failed to batch mark read");
    }
  };

  return (
    <div className="space-y-6">
      {/* Header & Filters */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h2 className="text-sm font-semibold text-foreground flex items-center gap-2">
            <MessageSquare className="size-4 text-violet-500" />
            Contact Inquiries &amp; Messages
          </h2>
          <p className="mt-0.5 text-xs text-muted-foreground">
            {total} total messages &middot;{" "}
            <span className="font-semibold text-violet-500">{unreadCount} unread</span>
          </p>
        </div>

        <div className="flex items-center gap-2">
          <select
            value={statusFilter}
            onChange={(e: any) => {
              setStatusFilter(e.target.value);
              setPage(1);
            }}
            className="h-8 rounded-md border border-border bg-card px-2.5 text-xs text-foreground font-medium outline-none"
          >
            <option value="all">All Messages</option>
            <option value="new">Unread Only</option>
            <option value="read">Read Only</option>
          </select>

          {unreadCount > 0 && (
            <Button
              variant="outline"
              size="sm"
              onClick={handleMarkAllRead}
              className="h-8 text-xs gap-1.5"
            >
              <CheckCheck className="size-3.5" />
              Mark Page Read
            </Button>
          )}
        </div>
      </div>

      {isLoading ? (
        <Card className="border-border p-12 text-center text-muted-foreground">
          <Loader2 className="mx-auto size-6 animate-spin text-brand mb-2" />
          Loading contact submissions…
        </Card>
      ) : submissions.length === 0 ? (
        <div className="rounded-xl border border-dashed border-border p-12 text-center">
          <MessageSquare className="mx-auto mb-3 size-6 text-muted-foreground" aria-hidden="true" />
          <p className="text-sm font-semibold text-foreground">No submissions found</p>
          <p className="mt-1 text-xs text-muted-foreground">
            {statusFilter === "new"
              ? "All caught up! No unread messages waiting."
              : "Submissions from the public contact page will appear here."}
          </p>
        </div>
      ) : (
        <div className="rounded-xl border border-border bg-card shadow-xs">
          <div className="divide-y divide-border">
            {submissions.map((s: any) => (
              <div
                key={s.id}
                className="flex flex-col gap-3 p-5 sm:flex-row sm:items-start sm:gap-4 hover:bg-muted/10 transition-colors"
              >
                <div className="min-w-0 flex-1 space-y-1.5">
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="text-xs font-semibold text-foreground">{s.name}</span>
                    <a
                      href={`mailto:${s.email}`}
                      className="inline-flex items-center gap-1 text-[11px] text-muted-foreground underline underline-offset-4 hover:text-violet-500 font-mono"
                    >
                      <Mail className="size-3" />
                      {s.email}
                    </a>
                    {s.status === "new" && (
                      <Badge
                        variant="outline"
                        className="bg-violet-500/10 text-violet-500 border-violet-500/30 text-[10px] font-semibold uppercase"
                      >
                        New
                      </Badge>
                    )}
                  </div>
                  <p className="text-xs font-semibold text-foreground">{s.subject}</p>
                  <p className="whitespace-pre-wrap text-xs leading-relaxed text-muted-foreground">
                    {s.message}
                  </p>
                  <p className="font-mono text-[10px] text-muted-foreground pt-1">
                    Received {new Date(s.createdAt).toLocaleString()}
                  </p>
                </div>

                <div className="flex items-center gap-1.5 shrink-0">
                  {s.status === "new" && (
                    <Button
                      variant="outline"
                      size="sm"
                      className="h-8 text-xs gap-1.5"
                      onClick={() => markReadMutation.mutate(s.id)}
                      disabled={markReadMutation.isPending}
                    >
                      <Check className="size-3" />
                      Mark read
                    </Button>
                  )}
                  <Button
                    variant="ghost"
                    size="sm"
                    className="size-8 p-0 text-muted-foreground hover:text-destructive"
                    title="Delete Submission"
                    onClick={() => {
                      if (confirm("Delete this contact message permanently?")) {
                        deleteMutation.mutate(s.id);
                      }
                    }}
                  >
                    <Trash2 className="size-3.5" />
                  </Button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-between pt-2">
          <Button
            variant="outline"
            size="sm"
            disabled={page <= 1}
            onClick={() => setPage((p) => Math.max(1, p - 1))}
            className="gap-1"
          >
            <ChevronLeft className="size-3.5" /> Previous
          </Button>
          <span className="text-xs text-muted-foreground">
            Page {page} of {totalPages} ({total} submissions)
          </span>
          <Button
            variant="outline"
            size="sm"
            disabled={page >= totalPages}
            onClick={() => setPage((p) => p + 1)}
            className="gap-1"
          >
            Next <ChevronRight className="size-3.5" />
          </Button>
        </div>
      )}
    </div>
  );
}
